/**
 * Single source of truth for the Classic betting vocabulary. Every betting
 * module (slice, stake engine, interaction controls, orchestrator, hook) and
 * the UI reference these constants — never raw strings. Same idiom as
 * `socketEvents.js` (named exports of `KEY:'KEY'` objects).
 */

// Per-panel bet lifecycle. Maps 1:1 to the action-button variant and drives the
// derived interaction controls. The machine is strictly linear:
//
//   IDLE -> BET_PLACED -> BET_SUBMITTED -> BET_ACCEPTED -> BET_RUNNING -> terminal
//           (FE only)     (PLACE_BET      (server acked)                  (CASHED_OUT
//            editable      emitted —                                       | CRASHED)
//            cancelable)   LOCKED)
//
// `BET_PLACED` is a PENDING bet that exists ONLY in frontend state — nothing has
// reached the server, so the player may still cancel it, change the stake, or
// re-place it. `BET_SUBMITTED` is the point of no return: PLACE_BET has left the
// client, so the bet is final. Nothing can move a panel backwards out of
// BET_SUBMITTED except the server refusing the bet outright.
export const BET_STATE = {
  IDLE: 'IDLE',
  BET_PLACED: 'BET_PLACED', // pending — frontend only
  BET_SUBMITTED: 'BET_SUBMITTED', // PLACE_BET emitted — locked
  BET_ACCEPTED: 'BET_ACCEPTED',
  BET_RUNNING: 'BET_RUNNING',
  BET_CASHED_OUT: 'BET_CASHED_OUT',
  CRASHED: 'CRASHED',
}

// Deferred backend submission. A pending bet is held in frontend state and the
// PLACE_BET emit fires LEAD_MS before the betting window closes (the round's
// takeoff), which is the last instant the server still accepts bets.
export const BET_SUBMISSION = {
  LEAD_MS: 2000,
}

// Manual vs auto betting. Driven by the Auto-Bet toggle; `mode === AUTO` means
// the toggle is on.
export const BET_MODE = {
  MANUAL: 'MANUAL',
  AUTO: 'AUTO',
}

// Generic on/off used by autoCashout.status, the interaction controls, and any
// future toggle.
export const TOGGLE = {
  ENABLED: 'ENABLED',
  DISABLED: 'DISABLED',
}

// Auto-bet loop runtime (only meaningful when mode === AUTO).
export const AUTOBET_CYCLE = {
  IDLE: 'IDLE', // not looping
  ACTIVE: 'ACTIVE', // re-places every round
  STOPPING: 'STOPPING', // stop requested; finish the current round then idle
}

// The three ways a stake value can change — the single `resolveStake` entry
// point branches on this.
export const STAKE_CONTROL = {
  CONTROL_BUTTON: 'CONTROL_BUTTON', // +/- stepper
  QUICK_STAKE: 'QUICK_STAKE', // preset chip
  DIRECT_INPUT: 'DIRECT_INPUT', // typed input
}

export const CONTROL_DIRECTION = {
  INCREMENT: 'INCREMENT',
  DECREMENT: 'DECREMENT',
}

// Auto-cashout multiplier target bounds (inclusive) + precision. The input snaps
// down above MAX while typing and clamps up to MIN on blur, so the stored value
// is always empty or within [MIN, MAX] at 2 decimals.
export const AUTO_CASHOUT = {
  MIN: 1.01,
  MAX: 150,
  DECIMALS: 2,
}

// What the single action button currently is. CANCEL only ever discards a
// PENDING (frontend-only) bet or stops the auto loop — it never reaches the
// server. Once the bet is submitted the button becomes the inert PLACED badge.
export const ACTION_VARIANT = {
  BET: 'BET',
  START_AUTO: 'START_AUTO', // AUTO mode, loop not yet running
  CANCEL: 'CANCEL', // discard a pending bet, or stop the auto loop
  PLACED: 'PLACED', // submitted & locked — inert, waiting for the round
  CASHOUT: 'CASHOUT',
}

// BET_SETTLED authoritative result (BE values).
export const BET_RESULT = {
  WON: 'WON',
  LOST: 'LOST',
  CASHED_OUT: 'CASHED_OUT',
}

// FE dual-bet panel ids. These match the socket `panel` field (1 | 2).
export const PANELS = {
  ONE: 1,
  TWO: 2,
}

export const PANEL_IDS = [PANELS.ONE, PANELS.TWO]

// Bet subType sent to the BE on PLACE_BET (mirrors the socketService contract).
export const BET_SUBTYPE = {
  MANUAL: 'MANUAL',
  AUTO: 'AUTO',
  FREEBET: 'FREEBET',
}

// PLACE_BET_RESPONSE failure codes. Always branch on `code`, never message text.
export const PLACE_BET_ERROR_CODE = {
  BET_NOT_ALLOWED_NOW: 'BET_NOT_ALLOWED_NOW', // round locked -> retry next round
  NO_ACTIVE_GAME: 'NO_ACTIVE_GAME', // no live round -> retry next round
  BET_LIMIT_EXCEEDED: 'BET_LIMIT_EXCEEDED', // per-round cap -> do NOT retry
  GAME_NOT_AVAILABLE: 'GAME_NOT_AVAILABLE',
  GAME_CODE_REQUIRED: 'GAME_CODE_REQUIRED',
  PLACE_BET_ERROR: 'PLACE_BET_ERROR', // generic fallback
}

// How the orchestrator reacts to a failed PLACE_BET.
export const PLACE_BET_ERROR_ACTION = {
  RETRY: 'RETRY', // keep the bet queued for the next round
  RESET: 'RESET', // drop the bet back to IDLE
}
